import { Component, EventEmitter, Input, Output } from '@angular/core';
import { jobDetails, jobDtlsResponse } from '@interfaces/job-details.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { VendorRepJobDetailsService } from './vendor-rep-job-details.service';
import { GlobalErrorService } from '@services/global-error.service';
import { FileDownloadService } from '@services/file-download.service';
import { JobDetErrorMsg, PersonaType } from '@constants/global.enum';
import { SearchService } from '@services/search.service';
import { dateFormat } from '@constants/dateFormat';
import { OpenPositionsService } from '../open-positions/open-positions.service';
import { jobStatus } from '@constants/constants';
import { PositionData } from '@interfaces/open-positions.model';

@Component({
  selector: 'app-vendor-rep-job-details',
  templateUrl: './vendor-rep-job-details.component.html',
  styleUrl: './vendor-rep-job-details.component.scss',
})
export class VendorRepJobDetailsComponent {
  // full candidate set for this position (client-side sorted/filtered/paged)
  rowData: jobDetails[] = [];
  displayData: jobDetails[] = [];
  totalCount = 0;
  filteredCount = 0;

  fieldsVisibility: string = PersonaType.VendorRepJobDet;
  hiringRequestId: number = 0;
  jobTitle: string = '';
  imageSrc: string = 'assets/img/no-data-found.png';
  errorMsg: string = JobDetErrorMsg.ErrorMessage;
  isDataLoaded: boolean = false;
  projectName: string = '';
  primarySkills: string = '';
  hiringManagerName: string = '';
  jobDescriptionFilePath: string = '';
  status: string = '';
  dateFormat = dateFormat;

  // edit / delete flows (preserved)
  edit = false;
  delete = false;
  candidateId: string = '';
  candidateName: string = '';
  location: string = '';
  addCandidateForm!: FormGroup;

  // view state (client-side)
  searchText = '';
  filterValue = '';
  pageSize = 10;
  currentPage = 1;
  sortColumn = '';
  sortDir: 'asc' | 'desc' = 'asc';
  showFilterPanel = false;
  filterDraft = { status: '', location: '' };
  activeFilters: { status?: string; location?: string } = {};
  hoverTip = { show: false, text: '', x: 0, y: 0 };

  @Input() openPositionData: PositionData | null = null;
  @Output() close = new EventEmitter<void>();

  constructor(
    private router: Router,
    public jobDetails: VendorRepJobDetailsService,
    private globalError: GlobalErrorService,
    private fileDownloadService: FileDownloadService,
    private searchService: SearchService,
    public fb: FormBuilder,
    private openPositionsService: OpenPositionsService,
  ) {}

  ngOnInit() {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.status = navigation.extras.state['status'];
    } else {
      this.status = history.state?.status;
    }
    if (this.openPositionData) {
      this.hiringRequestId = this.openPositionData.hiringRequestId;
      this.jobTitle = this.openPositionData.jobTitle;
      this.jobDescriptionFilePath = this.openPositionData.jobDescriptionPath;
      this.projectName = this.openPositionData.projectName;
      this.hiringManagerName = this.openPositionData.hiringManagerName;
      this.primarySkills = Array.isArray(this.openPositionData.skillName)
        ? this.openPositionData.skillName.join(', ')
        : this.openPositionData.skillName;
    }
    this.loadJobDetails();
  }

  private loadJobDetails(): void {
    if (!this.hiringRequestId) {
      this.isDataLoaded = true;
      return;
    }
    this.jobDetails.getJobDetails(this.hiringRequestId).subscribe(
      (res: jobDtlsResponse) => {
        this.rowData = Array.isArray(res.data?.candidates) ? res.data.candidates : [];
        this.isDataLoaded = true;
        this.applyView();
      },
      (error) => {
        this.isDataLoaded = true;
        this.globalError.logComponentError(error);
      },
    );
  }

  /** Recompute the visible page from search + filters + sort over rowData. */
  private applyView(): void {
    let rows = [...this.rowData];
    if (this.searchText) {
      rows = this.searchService.getSearchedData(rows, this.searchText.toLowerCase());
    }
    if (this.activeFilters.status) {
      const s = this.activeFilters.status.toLowerCase();
      rows = rows.filter((r) => (r.status || '').toLowerCase() === s);
    }
    if (this.activeFilters.location) {
      const l = this.activeFilters.location.toLowerCase();
      rows = rows.filter((r) => (this.rowLocation(r) || '').toLowerCase().includes(l));
    }
    if (this.sortColumn) {
      rows = this.sortRows(rows, this.sortColumn, this.sortDir);
    }
    this.filteredCount = rows.length;
    this.totalCount = rows.length;
    const maxPage = Math.max(1, Math.ceil(rows.length / this.pageSize));
    if (this.currentPage > maxPage) this.currentPage = maxPage;
    const start = (this.currentPage - 1) * this.pageSize;
    this.displayData = rows.slice(start, start + this.pageSize);
  }

  private sortRows(rows: jobDetails[], col: string, dir: 'asc' | 'desc'): jobDetails[] {
    const mult = dir === 'asc' ? 1 : -1;
    const dateCols = ['createdDate', 'earliestAvailability'];
    const numCols = ['yearsOfExperience'];
    return [...rows].sort((a: any, b: any) => {
      let av = a[col];
      let bv = b[col];
      if (dateCols.includes(col)) {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else if (numCols.includes(col)) {
        av = +av || 0; bv = +bv || 0;
      } else {
        av = (av ?? '').toString().toLowerCase();
        bv = (bv ?? '').toString().toLowerCase();
      }
      return av < bv ? -1 * mult : av > bv ? 1 * mult : 0;
    });
  }

  rowLocation(row: any): string {
    const loc = row?.location;
    if (!loc) return '';
    if (Array.isArray(loc)) {
      return loc.map((l: any) => (typeof l === 'string' ? l : l?.location || l?.name || '')).filter(Boolean).join(', ');
    }
    return String(loc);
  }

  // ---- search ----
  searchItem(event: KeyboardEvent) {
    this.searchText = this.searchService.getFilteredValue(event) || '';
    this.filterValue = this.searchText;
    this.currentPage = 1;
    this.applyView();
  }

  clearInput() {
    this.searchText = '';
    this.filterValue = '';
    this.currentPage = 1;
    this.applyView();
  }

  // ---- sort ----
  sortBy(field: string): void {
    if (this.sortColumn === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = field;
      this.sortDir = 'asc';
    }
    this.currentPage = 1;
    this.applyView();
  }

  sortIndicator(field: string): string {
    return this.sortColumn === field ? this.sortDir : '';
  }

  // ---- filter panel ----
  get facetStatus(): string[] {
    return [...new Set(this.rowData.map((r) => r.status).filter(Boolean))].sort();
  }
  get facetLocation(): string[] {
    return [...new Set(this.rowData.map((r) => this.rowLocation(r)).filter(Boolean))].sort();
  }
  get activeFilterCount(): number {
    return (this.activeFilters.status ? 1 : 0) + (this.activeFilters.location ? 1 : 0);
  }

  toggleFilterPanel(): void {
    if (!this.showFilterPanel) {
      this.filterDraft = {
        status: this.activeFilters.status || '',
        location: this.activeFilters.location || '',
      };
    }
    this.showFilterPanel = !this.showFilterPanel;
  }

  applyFilterPanel(): void {
    this.activeFilters = {
      status: this.filterDraft.status || undefined,
      location: this.filterDraft.location || undefined,
    };
    this.currentPage = 1;
    this.showFilterPanel = false;
    this.applyView();
  }

  resetFilterPanel(): void {
    this.filterDraft = { status: '', location: '' };
    this.activeFilters = {};
    this.currentPage = 1;
    this.showFilterPanel = false;
    this.applyView();
  }

  // ---- pagination (client-side) ----
  get totalPages(): number { return Math.max(1, Math.ceil((this.filteredCount || 0) / this.pageSize)); }
  get rangeStart(): number { return this.filteredCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; }
  get rangeEnd(): number { return Math.min(this.currentPage * this.pageSize, this.filteredCount); }

  get pageWindow(): number[] {
    const total = this.totalPages;
    const cur = this.currentPage;
    const out: number[] = [];
    if (total <= 7) { for (let i = 1; i <= total; i++) out.push(i); return out; }
    out.push(1);
    const start = Math.max(2, cur - 1);
    const end = Math.min(total - 1, cur + 1);
    if (start > 2) out.push(-1);
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push(-1);
    out.push(total);
    return out;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.applyView();
  }
  prevPage(): void { this.goToPage(this.currentPage - 1); }
  nextPage(): void { this.goToPage(this.currentPage + 1); }
  onPageSizeChange(event: Event): void {
    this.pageSize = +(event.target as HTMLSelectElement).value;
    this.currentPage = 1;
    this.applyView();
  }
  trackByIndex(index: number): number { return index; }

  // ---- cell helpers ----
  statusClass(status?: string): string {
    const v = (status || '').toLowerCase();
    if (v.includes('select') || v.includes('onboard') || v.includes('hire') || v.includes('join') || v.includes('offer')) return 'open';
    if (v.includes('reject') || v.includes('declin') || v.includes('withdraw') || v.includes('drop') || v.includes('backed')) return 'closed';
    return 'hold';
  }

  showTip(event: MouseEvent, text?: string | null): void {
    if (!text) return;
    const cell = event.currentTarget as HTMLElement;
    const inner = (cell.querySelector('.op-tip-txt') as HTMLElement) || cell;
    if (inner.scrollWidth <= inner.clientWidth + 1) return;
    const rect = cell.getBoundingClientRect();
    this.hoverTip = { show: true, text, x: rect.left, y: rect.bottom + 4 };
  }
  hideTip(): void { this.hoverTip.show = false; }

  // ---- preserved behaviours ----
  receiveData(data: any, candidateEmailId: string) {
    this.rowData.forEach((item) => {
      if (item.emailId === candidateEmailId) {
        item.candidateName = data.candidateName;
        item.contactNumber = data.contactNumber;
        item.emailId = data.emailId;
        item.experience = data.experience;
        item.location = data.location;
        item.earliestAvailability = data.earliestAvailability;
        item.resumePath = data.resumePath;
        item.additionalDocumentsPath = data.additionalDocumentsPath;
        item.url = data.url;
      }
    });
    this.applyView();
  }

  getCandidateInfor(candidateId: string) {
    this.edit = true;
    this.delete = false;
    this.candidateId = candidateId;
  }

  resetActions(_event: boolean) {
    this.edit = false;
    this.delete = false;
  }

  deleteCandidate(_event: boolean) {
    this.rowData = this.rowData.filter((item) => item.candidateId !== this.candidateId);
    this.applyView();
  }

  private getFileExtension(filePath: string): string {
    return filePath.split('.').pop()?.toLowerCase() || 'pdf';
  }

  jobDescription() {
    const fileExtension = this.getFileExtension(this.jobDescriptionFilePath);
    const fullFileName = `${this.jobTitle}_jobDescription.${fileExtension}`;
    this.fileDownloadService.downloadFile(this.jobDescriptionFilePath, fullFileName);
  }

  downloadFile(filePath: string, fileName: string | number, fileType: string) {
    const fileExtension = this.getFileExtension(filePath);
    const fileNameFormat = `${fileType}_${fileName}.${fileExtension}`;
    this.fileDownloadService.downloadFile(filePath, fileNameFormat);
  }

  isDisabled(eachRowStatus: string): boolean {
    if (this.status === jobStatus.CLOSED || this.status === jobStatus.ON_HOLD) {
      if (
        eachRowStatus !== jobStatus.JOINED &&
        eachRowStatus !== jobStatus.OPEN &&
        !jobStatus.DISABLED_STATUSES.includes(eachRowStatus)
      ) {
        return false;
      }
      return true;
    } else if (
      eachRowStatus === jobStatus.JOINED ||
      jobStatus.DISABLED_STATUSES.includes(eachRowStatus)
    ) {
      return true;
    }
    return false;
  }

  navigateBack() {
    this.close.emit();
  }
}
